import {shouldDefaultToBestMatch} from '@github-ui/issue-query-free-text'
import {replaceDateTokens} from '@github-ui/issue-url-helper'

const STATE_REASON_REGEX = /(?:^|\s)reason:"?(completed|not(\s|-)planned)"?(?:$|\s)/g

export type ScopedRepository = {owner: string; name: string; is_archived?: boolean}

export type ItemsType = 'issues' | 'pullRequests' | 'any'

// Takes in the user supplied query string,
// removes some duplicate and ignored tokens,
// and returns the modified query string.
//   ex: getQuery(query: "is:issue purple type:issue", scopedRepository?: true) =>
//   "is:issue purple repo:owner/repo sort:sort:created-desc"
// Warning, this function must stay in sync with its counterpart in the backend
// see app/helpers/issues_react_helper.rb#get_repo_query
export function getQuery(
  query: string,
  scopedRepository?: ScopedRepository,
  bestMatchSortEnabled?: boolean,
  requireFreeTextForBestMatch = false,
): string {
  const queryStringWithDates = replaceDateTokens(applyQueryDefaults(query, scopedRepository?.is_archived || false))

  if (!scopedRepository) {
    return queryStringWithDates
  }

  let cleanQuery = ''
  const parsedQuery = parseQuery(queryStringWithDates)
  const deduplicatedQuery = removeDuplicateValues(parsedQuery)
  // The relevance default is only meaningful once the user has typed something to be relevant to,
  // so behind `issues_best_match_requires_free_text` it needs real free text in the query. An
  // explicit `sort:` always wins. This must agree with `getDefaultSortKey` in SortingDropdown,
  // otherwise the list is ordered by one sort and labelled with another.
  const defaultSort = shouldDefaultToBestMatch(bestMatchSortEnabled, queryStringWithDates, requireFreeTextForBestMatch)
    ? 'sort:relevance-desc'
    : 'sort:created-desc'
  const sortQualifier = parsedQuery.has('sort') ? '' : defaultSort

  cleanQuery = queryStringWithDates
  const removedValues = mapDifference(parsedQuery, deduplicatedQuery)
  for (const [key, value] of removedValues) {
    const str = `${key}:${value}`
    cleanQuery = cleanQuery.replace(str, '')
  }
  cleanQuery = removeIgnoredQualifiers(cleanQuery)
  // Replace any whitespace with a single whitespace except for the filter values
  cleanQuery = cleanQuery
    .replace(/("[^"]*")|[ \t\r\n]+/g, (_, quoted) => {
      return quoted ? quoted : ' '
    })
    .trim()

  cleanQuery = cleanQuery.trim()
  const scopedRepoQualifier = `repo:${scopedRepository.owner}/${scopedRepository.name}`
  return [cleanQuery, scopedRepoQualifier, sortQualifier].join(' ').trim()
}

// Applying empty query logic - `archived:false`
export function applyQueryDefaults(query: string, isRepoArchived: boolean) {
  // If the repo is archived, we don't want to filter by archived:false since all the issues are archived
  if (isRepoArchived) {
    return query
  }

  return `${query === '' ? 'archived:false' : query}`
}

// Qualifiers where the backend search service does not support comma-separated values.
// The filter UI joins multiple selections with commas (e.g. repo:a,b), but the backend
// expects separate qualifiers joined with OR (repo:a OR repo:b).
// With advanced search, space-separated repo/org/user qualifiers use AND semantics,
// so we must use explicit OR to get the expected union behavior.
const EXPANDABLE_QUALIFIER_REGEX = /^(-?)(repo|org|user):(.+)/i

/**
 * Expands comma-separated values for qualifiers that the backend search service
 * does not support with comma syntax (repo, org, user).
 * Uses OR between expanded values because advanced search treats spaces as AND.
 *
 * @example
 * expandCommaSeparatedQualifiers("repo:github/foo,github/bar is:issue")
 * // => "(repo:github/foo OR repo:github/bar) is:issue"
 *
 * expandCommaSeparatedQualifiers("user:alice,bob")
 * // => "(user:alice OR user:bob)"
 *
 * expandCommaSeparatedQualifiers("-repo:github/foo,github/bar")
 * // => "-repo:github/foo -repo:github/bar"
 */
export function expandCommaSeparatedQualifiers(query: string): string {
  // Split by whitespace, preserving quoted strings
  const tokens = query.match(/((?:[^\s"]+|"[^"]*")+)/g)
  if (!tokens) return query

  const expanded: string[] = []

  for (const token of tokens) {
    const match = token.match(EXPANDABLE_QUALIFIER_REGEX)
    if (!match) {
      expanded.push(token)
      continue
    }

    const [, negation, qualifier, value] = match

    // Don't expand if the entire value is quoted
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (value!.startsWith('"') && value!.endsWith('"')) {
      expanded.push(token)
      continue
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const values = value!.split(',').filter(v => v.length > 0)
    if (values.length === 1) {
      expanded.push(token)
    } else if (negation) {
      // Negated qualifiers: -repo:a,b => -repo:a -repo:b (AND semantics is correct for exclusion)
      for (const v of values) {
        expanded.push(`${negation}${qualifier}:${v}`)
      }
    } else {
      // Positive qualifiers: repo:a,b => (repo:a OR repo:b)
      const orGroup = values.map(v => `${qualifier}:${v}`).join(' OR ')
      expanded.push(`(${orGroup})`)
    }
  }

  return expanded.join(' ')
}

export function checkIfStateReasonPresent(query: string) {
  return query.match(STATE_REASON_REGEX)
}

/**
 * Parse the string query into a map of search tokens
 * Values that don't contain a colon are treated as a search term and stored with the `searchTerms` key
 *
 * @param query GitHub search query
 * @returns Map of search tokens + search terms
 * @example
 * const queryMap = parseQuery('repo:github/github')
 * {
 *  repo: ['github.com'],
 *  searchTerms: []
 * }
 *
 * const queryMap = parseQuery('repo:github/github repo:github/issues state:open label:bug fun issue')
 * {
 *  repo: ['github/github', 'github/issues'],
 *  state: ['open'],
 *  label: ['bug']
 *  searchTerms: ['fun', 'issue']
 * }
 */
export function parseQuery(query: string): Map<string, string[]> {
  const queryMap: Map<string, string[]> = new Map()
  queryMap.set('searchTerms', [])

  const matchBySpaceIgnoreQuotedExp = /((?:[^\s"]+|"[^"]*")+)/g
  const tokens = query
    .trim()
    .split(matchBySpaceIgnoreQuotedExp)
    .filter(item => item.trim() !== '') // Remove duds

  for (const token of tokens) {
    const [key, value] = token.split(/:(.*)/s) // split on first occurence of : in token

    // Contains ':' so it's a filter token
    if (key && value) {
      if (!queryMap.has(key)) {
        queryMap.set(key, [value])
        continue
      }

      // We can have more than one usage of the filter, so we append if the key exists
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      queryMap.set(key, [...queryMap.get(key)!, value])
      continue
    }

    // Values without ':' are considered search terms
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryMap.set('searchTerms', [...queryMap.get('searchTerms')!, token])
  }

  return queryMap
}

export function findSortReactionInQuery(query: string): boolean {
  if (!query) return false
  const parsedQuery = parseQuery(query)
  const sort = parsedQuery.get('sort')

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return (sort && sort.length === 1 && sort[0]!.startsWith('reactions-')) || false
}

export function replaceSortInQuery(query: string, textToReplace: string): string {
  // replace current sort:{value} in query with the selected one
  return query
    .replace(/\bsort\S+/gi, '')
    .concat(` ${textToReplace}`)
    .replaceAll('  ', ' ')
}

export function checkIfQuerySupportsPr(query: string) {
  const parsedQuery = parseQuery(query)
  const searchTerms = new Set([...(parsedQuery.get('is') || []), ...(parsedQuery.get('type') || [])])
  // Query contains PR search terms if it doesn't specify the type, or it does not include the type issue,
  // or if it specifies the type as PR or pull-request
  return searchTerms.size === 0 || !searchTerms.has('issue') || searchTerms.has('pr') || searchTerms.has('pull-request')
}

function removeDuplicateValues(values: Map<string, string[]>): Map<string, string[]> {
  const newQueryMap = new Map(values)
  const valuesForIs = newQueryMap.get('is') || []
  const valuesForType = newQueryMap.get('type') || []

  const hasTypeValue: Record<string, boolean> = {
    issue: valuesForIs.includes('issue'),
    pr: valuesForIs.includes('pr'),
  }

  if (!valuesForIs.length) {
    newQueryMap.delete('is')
  }

  // do not add type:pr or type:issue to the query if is:pr or is:issue is already present
  const newValues: string[] = valuesForType.filter(value => !hasTypeValue[value])

  if (newValues.length > 0) {
    newQueryMap.set('type', newValues)
  } else {
    newQueryMap.delete('type')
  }
  return newQueryMap
}

// For compatibility, remove qualifiers about repo, org, or user
// Also remove search terms (not filters) with user handles
// e.g., remove "@monalisa" but preserve "assignee:@monalisa"
function removeIgnoredQualifiers(queryString: string) {
  // Split the query string by space, but preserve label titles with spaces (within double quotes)
  const splitQueryString = queryString.match(/(".*?"|[^"\s]+)+/g) ?? []
  const ignoredQualifiersRegex = /^(repo:|org:|user:|@)/

  return splitQueryString.filter(v => !ignoredQualifiersRegex.test(v)).join(' ')
}

// Returns a map representing the key:value pairs present in map1 that are not in map2
function mapDifference(map1: Map<string, string[] | string>, map2: Map<string, string[] | string>) {
  const diff = new Map()
  for (const [key, val] of map1) {
    if (!map2.has(key)) {
      diff.set(key, val)
    }
  }
  return diff
}

export function isPrsOnly(activeSearchQuery: string) {
  const parsed = parseQuery(activeSearchQuery)
  const isData = parsed.get('is') || []
  const typeData = parsed.get('type') || []
  const combinedData = [...isData, ...typeData]
  const combinedDataMap = new Map(combinedData.map(str => [str, true]))
  const hasPrQualifier = combinedDataMap.has('pr') || combinedDataMap.has('pull-request')
  const hasIssueQualifier = combinedDataMap.has('issue') || combinedDataMap.has('issues')
  return hasPrQualifier && !hasIssueQualifier
}
